import { Visibility, VisibilityOff } from '@mui/icons-material';
import {
  Alert,
  FormControlLabel,
  Grid,
  IconButton,
  InputAdornment,
  MenuItem,
  Switch,
  TextField,
  Typography,
} from '@mui/material';
import CpfInput from 'components/masked-input/CpfInput';
import PhoneInput from 'components/masked-input/PhoneInput';
import React, { useEffect, useRef, useState } from 'react';
import { User } from 'types/user';
import { UserProfile } from 'types/userProfile';
import { UserValidation } from '../validation/useUserValidation';
import KeyboardDatePicker from 'components/pickers/KeyboardDatePicker';
import { RecordSection } from 'components/record-shell/RecordShell';
import { asInputComponent } from 'components/masked-input/asInputComponent';

export interface UserForm extends User {
  password: string;
  password_confirmation: string;
}

interface UserFormProps {
  user: UserForm;
  handleChange: <K extends keyof UserForm>(index: K, value: UserForm[K]) => void;
  validation: UserValidation;
  userProfiles: UserProfile[];
  type: 'edit' | 'new';
  // o próprio usuário logado não pode se inativar
  isSelf?: boolean;
}

function PasswordField(props: {
  label: string;
  placeholder: string;
  value: string;
  onChange(value: string): void;
  error?: string;
  inputRef: React.RefObject<HTMLInputElement | null>;
}) {
  const [visible, setVisible] = useState(false);

  return (
    <TextField
      fullWidth
      inputRef={props.inputRef}
      error={!!props.error}
      helperText={props.error}
      label={props.label}
      placeholder={props.placeholder}
      value={props.value}
      onChange={e => props.onChange(e.target.value)}
      type={visible ? 'text' : 'password'}
      autoComplete="new-password"
      slotProps={{
        input: {
          endAdornment: (
            <InputAdornment position="end">
              <IconButton size="small" aria-label="Mostrar senha" onClick={() => setVisible(!visible)}>
                {visible ? <VisibilityOff color="primary" /> : <Visibility color="primary" />}
              </IconButton>
            </InputAdornment>
          ),
        },
      }}
    />
  );
}

const UserForm: React.FC<UserFormProps> = ({ user, handleChange, validation, userProfiles, type, isSelf }) => {
  const inputs = {
    username: useRef<HTMLInputElement>(null),
    name: useRef<HTMLInputElement>(null),
    rg: useRef<HTMLInputElement>(null),
    document_number: useRef<HTMLInputElement>(null),
    email: useRef<HTMLInputElement>(null),
    password: useRef<HTMLInputElement>(null),
    password_confirmation: useRef<HTMLInputElement>(null),
  };

  useEffect(() => {
    const [key] = Object.keys(validation) as [keyof typeof inputs];

    if (!key) return;

    inputs[key].current?.focus();
  }, [validation]);

  const profile = userProfiles.find(item => item.id === user.user_profile_id);

  return (
    <Grid container size={12} spacing={2} maxWidth={900}>
      <Grid size={12}>
        <Typography variant="subtitle2" fontWeight={700} color="primary">
          Identificação
        </Typography>
      </Grid>

      <Grid size={{ xs: 12, md: 6 }}>
        <TextField
          fullWidth
          autoFocus
          required
          inputRef={inputs.name}
          label="Nome completo"
          placeholder="Digite o nome completo do usuário"
          value={user.name}
          onChange={e => handleChange('name', e.target.value)}
          error={!!validation.name}
          helperText={validation.name}
        />
      </Grid>

      <Grid size={{ xs: 12, md: 3 }}>
        <TextField
          fullWidth
          required
          inputRef={inputs.document_number}
          error={!!validation.document_number}
          helperText={validation.document_number}
          label="CPF"
          placeholder="Digite o CPF"
          value={user.document_number}
          onChange={e => handleChange('document_number', e.target.value)}
          slotProps={{
            input: {
              inputComponent: asInputComponent(CpfInput),
            },
          }}
        />
      </Grid>

      <Grid size={{ xs: 12, md: 3 }}>
        <TextField
          fullWidth
          required
          inputRef={inputs.rg}
          error={!!validation.rg}
          helperText={validation.rg}
          label="RG"
          placeholder="Digite o RG"
          value={user.rg}
          onChange={e => handleChange('rg', e.target.value)}
        />
      </Grid>

      <Grid size={{ xs: 12, md: 4 }}>
        <TextField
          fullWidth
          label="Cargo"
          placeholder="Digite o cargo do usuário"
          value={user.role ?? ''}
          onChange={e => handleChange('role', e.target.value)}
        />
      </Grid>

      <Grid size={{ xs: 12, md: 4 }}>
        <KeyboardDatePicker
          fullWidth
          disableFuture
          label="Data de nascimento"
          value={user.birth_date}
          onChange={date => handleChange('birth_date', date)}
        />
      </Grid>

      <Grid size={{ xs: 12, md: 4 }}>
        <TextField
          fullWidth
          label="Telefone"
          placeholder="Digite o telefone do usuário"
          value={user.phone ?? ''}
          onChange={e => handleChange('phone', e.target.value)}
          slotProps={{
            input: {
              inputComponent: asInputComponent(PhoneInput),
            },
          }}
        />
      </Grid>

      <Grid size={12}>
        <TextField
          fullWidth
          multiline
          minRows={2}
          label="Endereço"
          placeholder="Digite o endereço do usuário"
          value={user.address ?? ''}
          onChange={e => handleChange('address', e.target.value)}
        />
      </Grid>

      <Grid size={12}>
        <RecordSection title="Acesso ao sistema">
          <Grid container spacing={2}>
            <Grid size={{ xs: 12, md: 4 }}>
              <TextField
                fullWidth
                required
                inputRef={inputs.username}
                label="Usuário"
                placeholder="Digite o nome do usuário"
                value={user.username}
                onChange={e => handleChange('username', e.target.value)}
                error={!!validation.username}
                helperText={validation.username || 'Serve como login'}
                disabled={type === 'edit'}
              />
            </Grid>

            <Grid size={{ xs: 12, md: 4 }}>
              <TextField
                fullWidth
                required
                inputRef={inputs.email}
                label="E-mail"
                placeholder="Digite o e-mail do usuário"
                value={user.email}
                onChange={e => handleChange('email', e.target.value)}
                error={!!validation.email}
                helperText={validation.email || 'Também serve como login'}
                disabled={type === 'edit'}
              />
            </Grid>

            <Grid size={{ xs: 12, md: 4 }}>
              <TextField
                select
                fullWidth
                label="Perfil de acesso"
                value={user.user_profile_id || ''}
                onChange={e => handleChange('user_profile_id', Number(e.target.value))}
                helperText={profile ? `${profile.access_rules.length} permissões neste perfil` : 'Escolha um perfil'}
              >
                {userProfiles.map(item => (
                  <MenuItem key={item.id} value={item.id}>
                    {item.name}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>

            <Grid size={{ xs: 12, md: 6 }}>
              <PasswordField
                inputRef={inputs.password}
                error={validation.password}
                label={type === 'edit' ? 'Nova senha' : 'Senha'}
                placeholder={type === 'edit' ? 'Deixe vazio para manter a atual' : 'Digite a senha'}
                value={user.password}
                onChange={value => handleChange('password', value)}
              />
            </Grid>

            <Grid size={{ xs: 12, md: 6 }}>
              <PasswordField
                inputRef={inputs.password_confirmation}
                error={validation.password_confirmation}
                label="Repita a senha"
                placeholder="Repita a senha"
                value={user.password_confirmation}
                onChange={value => handleChange('password_confirmation', value)}
              />
            </Grid>
          </Grid>
        </RecordSection>
      </Grid>

      <Grid size={12}>
        <FormControlLabel
          label="Ativo"
          control={
            <Switch
              checked={!!user.active}
              disabled={isSelf}
              onChange={e => handleChange('active', e.target.checked ? 1 : 0)}
            />
          }
        />
        <Alert severity="info" variant="outlined" sx={{ mt: 1 }}>
          Usuário inativo não consegue entrar no sistema.
          {isSelf && ' Você não pode inativar o próprio usuário.'}
        </Alert>
      </Grid>
    </Grid>
  );
};

export default UserForm;
